<template>
  <div>
    <poppins-card ref="targetElement" class="px-16 py-24 md-1:px-24">
      <div class="flex gap-16 items-center">
        <a
          v-if="editModeActive"
          class="cursor-pointer relative"
          :href="`/sitter/photo_redirect?return_to_profile=true&care_type=${paramData.careType}`"
        >
          <poppins-avatar
            :image-url="profileAccountData.photoUrl"
            :photo-status="profileAccountData.photoStatus"
            :size="AvatarSize.MEGA"
            :name="profileAccountData.displayName"
          />
          <div
            class="primary-focus flex justify-center items-center w-32 h-32 rounded-full bg-grey-divider-subdued !absolute top-64 right-0"
            aria-label="Edit profile photo"
          >
            <photo-icon :height="18.13" :width="18.13" color="#4B4C52" />
          </div>
        </a>
        <a v-else class="cursor-pointer relative" @click="handlePhotoClick">
          <poppins-avatar
            :image-url="profileAccountData.photoUrl"
            :photo-status="profileAccountData.photoStatus"
            :size="AvatarSize.MEGA"
            :name="profileAccountData.displayName"
          />
        </a>
        <div>
          <h2 class="text-h3 break-all">{{ displayName }}</h2>
          <star-rating
            v-if="reviewsData"
            :count="reviewsData.totalReviewCount"
            :average="reviewsData.starAggregate"
            class="cursor-pointer"
            @star-rating-click="$emit('star-rating-click')"
          />
        </div>
      </div>
      <div class="border-b-1 border-solid border-grey-divider-subdued mt-4 pb-12">
        <div class="flex flex-wrap align-center items-center">
          <sitter-badge-list
            :badges="profileData.badges"
            :idv="profileData.idv"
            :badge-size="58"
            :checks-only="true"
            :include-inactive-badges="true"
            class="cursor-pointer mt-8"
            @check-click="$emit('check-click')"
          />
          <sitter-badge-list
            v-if="showCertificationBadges"
            :badges="modifiedCertificationBadges"
            :badge-size="58"
            :certifications-only="true"
            :include-inactive-badges="false"
            class="cursor-pointer mt-8"
            @certification-click="$emit('certification-click')"
          />
        </div>
        <poppins-button
          v-if="showBgcEditBtn"
          value="Add safety check"
          size="small"
          class="mt-16"
          href="/account/providers/background-checks"
        />
        <!-- eslint-disable-next-line vue/no-v-html -->
        <p class="text-small text-grey-text-subdued mt-8" v-html="badgeSubtext" />
      </div>
      <icon-list
        :items="iconListItems"
        class="mt-16"
        :custom-margin="16"
        :edit-mode="editModeActive"
        @item-click="(item) => item.clickHandler()"
      >
        <template #icon="{ item }">
          <a v-if="item.editLink" :href="item.editLink" class="ml-auto">
            <pencil-icon v-if="editModeActive && item.editLink" :width="16" :height="16" />
          </a>
          <router-link v-else-if="item.to" :to="item.to" class="ml-auto">
            <pencil-icon v-if="editModeActive && item.to" :width="16" :height="16" />
          </router-link>
        </template>
      </icon-list>
      <provider-profile-contact-ctas
        v-if="!editModeActive && !previewModeActive"
        :profile-account-data="profileAccountData"
        :account-type-info="accountTypeInfo"
        :care-type="paramData.careType"
      />
      <poppins-button
        v-else-if="buttonInfo.show"
        :value="buttonInfo.value"
        :href="buttonInfo.href"
        class="w-full mt-16"
        size="large"
        @click="buttonInfo.click"
      />
    </poppins-card>
    <div
      v-if="scrolledPast && isStickyWidth"
      class="fixed left-0 bg-white w-full z-[60] p-16 border-t-1 border-b-1 border-solid border-grey-divider-subdued"
      :class="accountTypeInfo.isParent ? 'bottom-80' : 'bottom-0'"
    >
      <div class="flex gap-16">
        <poppins-avatar
          :image-url="profileAccountData.photoUrl"
          :photo-status="profileAccountData.photoStatus"
          :size="AvatarSize.LARGE"
          :name="profileAccountData.displayName"
        />
        <div>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <p @click="$emit('pay-rate-click')" v-html="stickyRate" />
          <star-rating
            v-if="reviewsData"
            :count="reviewsData.totalReviewCount"
            :average="reviewsData.starAggregate"
            @star-rating-click="$emit('star-rating-click')"
          />
        </div>
        <div v-if="accountTypeInfo.isParent" class="w-full text-right self-center mr-16">
          <provider-profile-favorite-sitter-button
            :favorited-sitter="favoritedSitter"
            :sitter-account-id="sitterAccountId"
            @favorited-sitter-change="favoritedSitterChange"
          />
        </div>
      </div>
      <provider-profile-contact-ctas
        v-if="!editModeActive"
        :profile-account-data="profileAccountData"
        :account-type-info="accountTypeInfo"
        :care-type="paramData.careType"
        :show-primary-only="true"
      />
      <poppins-button
        v-else-if="buttonInfo.show"
        :value="buttonInfo.value"
        :href="buttonInfo.href"
        class="w-full mt-16"
        size="large"
        @click="buttonInfo.click"
      />
    </div>
  </div>
</template>

<script lang="ts">
import pluralize from 'pluralize';
import PoppinsCard from '@/components/card/Card.vue';
import PoppinsButton from '@/components/button/Button.vue';
import PoppinsAvatar, { AvatarSize } from '@/components/avatar/Avatar.vue';
import StarRating from '@/components/star_rating/StarRating.vue';
import IconList from '@/components/icon_list/IconList.vue';
import ProviderProfileFavoriteSitterButton from './ProviderProfileFavoriteSitterButton.vue';
import SitterBadgeList from '@/components/sitter_badge_list/SitterBadgeList.vue';
import { sendSegmentEvent } from '@/lib/segment';
import HeartOutlineIcon from '@/graphics/icons/HeartOutlineIcon.vue';
import PlaceOutlineIcon from '@/graphics/icons/PlaceOutlineIcon.vue';
import DollarIcon from '@/graphics/icons/DollarIcon.vue';
import LoggedInIcon from '@/graphics/icons/LoggedInIcon.vue';
import PhotoIcon from '@/graphics/icons/PhotoIcon.vue';
import PencilIcon from '@/graphics/icons/PencilIcon.vue';
import ProviderProfileContactCtas from './ProviderProfileContactCtas.vue';
import { usesSpaRoutes, getEditUrl } from '../helpers/profileEditUrlHelper';

export default {
  name: 'ProviderProfileSitterSnapshot',
  components: {
    PoppinsCard,
    PoppinsButton,
    IconList,
    PoppinsAvatar,
    StarRating,
    ProviderProfileFavoriteSitterButton,
    SitterBadgeList,
    PhotoIcon,
    PencilIcon,
    ProviderProfileContactCtas,
  },
  props: {
    favoritedSitter: {
      default: false,
      type: Boolean,
    },
    accountTypeInfo: {
      default: () => ({}),
      type: Object,
    },
    profileAccountData: {
      default: () => ({}),
      type: Object,
    },
    profileData: {
      default: () => ({}),
      type: Object,
    },
    isProfileOwner: {
      type: Boolean,
      default: false,
    },
    reviewsData: {
      default: () => ({}),
      type: Object,
    },
    paramData: {
      default: () => ({}),
      type: Object,
    },
    editModeActive: {
      type: Boolean,
      default: false,
    },
    previewModeActive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      AvatarSize,
      scrolledPast: false,
      windowWidth: window.innerWidth,
      showProfilePhotoPopup: true,
    };
  },
  computed: {
    // TODO - clean up this logic after all care types have been migrated to the new edit flow.
    shouldUseSpaRoutes(): boolean {
      return usesSpaRoutes(this.paramData.careType);
    },
    isStickyWidth() {
      return this.windowWidth < 768;
    },
    sitterAccountId(): string {
      return this.profileAccountData?.id || '';
    },
    stickyRate(): any {
      return `<span class="text-body text-grey-text-subdued align-top">$</span> ${this.rateDisplay}`;
    },
    displayName(): string {
      return this.profileAccountData?.displayName || '';
    },
    buttonInfo(): object {
      let show = !this.accountTypeInfo.isSitter || this.isProfileOwner;
      if (this.editModeActive) {
        show = false;
      }
      let value = '';
      let href = '';
      let click = () => {};

      if (this.editModeActive || (!this.previewModeActive && this.isProfileOwner)) {
        value = 'Edit Profile';
        href = `/profile/provider/${this.paramData.careType}/edit_entry?source=profile`;
        click = () => this.trackEditProfileClick();
      } else if (this.previewModeActive) {
        value = 'Contact Sitter';
        click = () => this.$emit('show-edit-mode-popup', 'PREVIEW_CONTACT');
      } else {
        value = 'Contact Sitter';
        click = () => {
          this.trackContactButtonClick();
          this.$emit('togglePopup');
        };
      }
      return { value, click, href, show };
    },
    baseURL(): string {
      return `/profile/provider/${this.paramData.careType}`;
    },
    genericCareTypeForUrls(): string {
      return this.paramData.careType === 'babysitter' || this.paramData.careType === 'nanny'
        ? 'child_care'
        : this.paramData.careType;
    },
    iconListItems(): any {
      const locationEditLink = '/account/settings';
      const experienceEditLink =
        this.paramData.careType === 'housekeeping'
          ? `${this.baseURL}/housekeeping_experience_and_qualifications/edit?return_to_profile=true`
          : `${this.baseURL}/${this.genericCareTypeForUrls}_experience/edit?return_to_profile=true`;
      const rateEditLink =
        this.paramData.careType === 'housekeeping'
          ? `${this.baseURL}/housekeeping_bio_and_preferences/edit?return_to_profile=true`
          : `${this.baseURL}/${this.paramData.careType}_profile/edit?return_to_profile=true`;
      const items = [
        {
          icon: HeartOutlineIcon,
          text: this.yearsExperienceDisplay,
          editLink: this.shouldUseSpaRoutes ? null : experienceEditLink,
          to: this.shouldUseSpaRoutes ? getEditUrl(this.paramData.careType, 'experience', experienceEditLink) : null,
          clickHandler: () => {
            this.$emit('experience-click');
          },
        },
        {
          icon: PlaceOutlineIcon,
          text: this.locationDisplay,
          editLink: locationEditLink,
        },
        {
          icon: DollarIcon,
          text: this.rateDisplay,
          editLink: this.shouldUseSpaRoutes ? null : rateEditLink,
          to: this.shouldUseSpaRoutes ? getEditUrl(this.paramData.careType, 'rates', rateEditLink) : null,
          clickHandler: () => {
            this.$emit('pay-rate-click');
          },
        },
      ] as any;
      if (!this.editModeActive) {
        items.push({
          icon: LoggedInIcon,
          text: this.loggedInDisplay,
        });
      }
      return items;
    },
    yearsExperienceDisplay(): string {
      const { yearsExperience } = this.profileData || {};
      if (!yearsExperience) return '';
      return `${yearsExperience} ${pluralize('year', yearsExperience)} of experience`;
    },
    locationDisplay(): string {
      const { cityname, stateabbr } = this.profileAccountData?.location || {};
      if (!cityname || !stateabbr) return '';
      const distanceFrom = this.profileAccountData?.distanceFrom;
      const formattedDistance = Number(distanceFrom) < 1 ? '< 1' : distanceFrom;
      return distanceFrom ? `${formattedDistance} mi • ${cityname}, ${stateabbr}` : `${cityname}, ${stateabbr}`;
    },
    rateDisplay(): string {
      const convertedRate = (rate) => Math.ceil(rate / 100);

      if (this.paramData.careType === 'pet_care') {
        const { visitRate } = this.profileData?.rates || {};
        const rateText = convertedRate(visitRate);
        if (!visitRate) return '';
        return `<span class="text-h4">${rateText}</span><span class="text-body">/visit</span>`;
      }

      if (this.paramData.careType === 'companion_care') {
        const { fullDay } = this.profileData?.rates?.homeCareRates || {};
        const rateText = convertedRate(fullDay);
        if (!fullDay) return '';
        return `<span class="text-h4">${rateText}</span><span class="text-body">/day</span>`;
      }

      const { min, max, timeUnit } = this.profileData?.payRate || {};
      const rateText = `${convertedRate(min)}–${convertedRate(max)}`;
      const unit = timeUnit === 'hour' ? 'hr' : timeUnit;
      if (!min || !max) return '';
      return `<span class="text-h4">${rateText}</span><span class="text-body text-grey-text-subdued align-top">/${unit}</span>`;
    },
    loggedInDisplay(): string {
      const { lastLogin } = this.profileAccountData;
      if (!lastLogin) return '';
      return `Last login ${lastLogin.toLowerCase()}`;
    },
    badgeSubtext(): string {
      if (!this.editModeActive) return 'Select a badge to see details';
      if (!this.showCertificationBadges) return '';
      if (this.hasBadge('first_aid_training') && this.hasBadge('cpr_certified')) return '';
      const editLink = `${this.baseURL}/${this.genericCareTypeForUrls}_professional_skills/edit?return_to_profile=true`;
      return `<a class="underline" href="${editLink}">Certifications</a> will show here too.`;
    },
    showCertificationBadges(): boolean {
      const { careType } = this.paramData;
      // Housekeeping and pet care profiles should not display certification badges
      return !(careType === 'housekeeping' || careType === 'pet_care');
    },
    hasCompanionCareCertifications(): boolean {
      const { careType } = this.paramData;
      const { qualifications } = this.profileData;

      if (careType === 'companion_care') {
        // For companion care, check the qualifications array directly
        return (
          qualifications?.some((qualification) => qualification === 'FIRST_AID_TRAINING' || qualification === 'CPR_CERTIFIED') ||
          false
        );
      }

      return false;
    },
    modifiedCertificationBadges() {
      const { badges, qualifications } = this.profileData;
      const { careType } = this.paramData;

      if (!badges) return [];

      if (careType === 'companion_care') {
        // For companion care, update badge statuses based on qualifications array
        const hasFirstAidTraining = qualifications?.includes('FIRST_AID_TRAINING') || false;
        const hasCPRCertified = qualifications?.includes('CPR_CERTIFIED') || false;

        // Map existing badges and update statuses based on qualifications
        const modifiedBadges = badges.map((badge) => {
          if (badge.name === 'first_aid_training' && hasFirstAidTraining) {
            return { ...badge, status: 'COMPLETE' };
          }
          if (badge.name === 'cpr_certified' && hasCPRCertified) {
            return { ...badge, status: 'COMPLETE' };
          }
          return badge;
        });

        // Add missing badges if they don't exist
        if (hasFirstAidTraining && !modifiedBadges.some((badge) => badge.name === 'first_aid_training')) {
          modifiedBadges.push({ name: 'first_aid_training', status: 'COMPLETE' });
        }
        if (hasCPRCertified && !modifiedBadges.some((badge) => badge.name === 'cpr_certified')) {
          modifiedBadges.push({ name: 'cpr_certified', status: 'COMPLETE' });
        }

        return modifiedBadges;
      }

      return badges;
    },
    showBgcEditBtn(): boolean {
      if (!this.editModeActive) return false;
      if (this.hasBadge('enhanced_criminal') && this.hasBadge('motor_vehicle')) return false;
      return true;
    },
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.$nextTick(() => {
      window.addEventListener('resize', this.updateScreenSize);
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateScreenSize);
  },
  methods: {
    trackContactButtonClick(): void {
      sendSegmentEvent('Profile Contact Sitter Clicked', {
        sitter_uuid: this.sitterAccountId,
        care_type: this.paramData.careType,
      });
    },
    trackEditProfileClick(): void {
      sendSegmentEvent('Sitter Profile Edit Profile Clicked', {
        sitter_uuid: this.sitterAccountId,
        care_type: this.paramData.careType,
      });
    },
    updateScreenSize() {
      this.windowWidth = window.innerWidth;
    },
    favoritedSitterChange(favoritedSitter) {
      this.$emit('favoritedSitterChange', favoritedSitter);
    },
    handleScroll() {
      const { targetElement } = this.$refs as any;
      const rect = targetElement?.$el.getBoundingClientRect();
      this.scrolledPast = rect?.bottom < 0;
    },
    handleStickyButtonClick() {
      this.trackContactButtonClick();
    },
    handleMainButtonClick() {
      this.trackContactButtonClick();
    },
    hasBadge(badgeName: string): boolean {
      return this.modifiedCertificationBadges.some((b) => b.name === badgeName && b.status === 'COMPLETE');
    },
    handlePhotoClick() {
      this.$emit('show-photo-popup');
    },
  },
};
</script>
